import React from 'react';
import { motion } from 'framer-motion';
import { HelpCircle, MessageCircle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const DoubtActivityChart = ({ doubtAnalysis }) => {
  if (!doubtAnalysis || doubtAnalysis.totalDoubts === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
      >
        <p className="text-gray-500 dark:text-gray-400">No doubt activity data available</p>
      </motion.div>
    );
  }

  const { totalDoubts, averageResolutionTime, topConfusionAreas, doubtFrequency, engagementScore } = doubtAnalysis;

  const chartData = topConfusionAreas.map(area => ({
    topic: area.topic.length > 15 ? area.topic.substring(0, 15) + '...' : area.topic,
    doubts: area.doubtCount
  }));

  const getFrequencyColor = (frequency) => {
    switch (frequency) {
      case 'high':
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
      case 'moderate':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'low':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-indigo-600 rounded-lg">
            <HelpCircle className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">Doubt Activity</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">{totalDoubts} doubts asked</p>
          </div>
        </div>
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getFrequencyColor(doubtFrequency)}`}>
          {doubtFrequency} frequency
        </span>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-800/20 rounded-lg p-4 text-center"
        >
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Total Doubts</p>
          <p className="text-3xl font-bold text-indigo-600">{totalDoubts}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-lg p-4 text-center"
        >
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Avg. Resolution (hrs)</p>
          <p className="text-3xl font-bold text-purple-600">{averageResolutionTime}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg p-4 text-center"
        >
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Engagement</p>
          <p className="text-3xl font-bold text-blue-600">{engagementScore}%</p>
        </motion.div>
      </div>

      {/* Chart */}
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Top Confusion Areas</h4>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="topic" tick={{ fill: '#6b7280', fontSize: 11 }} angle={-45} textAnchor="end" height={80} />
            <YAxis tick={{ fill: '#6b7280', fontSize: 12 }} />
            <Tooltip
              contentStyle={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                border: 'none',
                borderRadius: '8px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
              }}
            />
            <Bar dataKey="doubts" fill="#6366f1" radius={[8, 8, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </motion.div>
  );
};

export default DoubtActivityChart;
